import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, Platform } from 'react-native';
import { Image } from 'expo-image';
import { UserCircle2 } from 'lucide-react-native';
import { useTheme, TYPOGRAPHY } from '../../theme';

interface AvatarProps {
  size?: number;
  name?: string;
  imageUrl?: string | null;
  backgroundColor?: string;
  /** Render a neutral silhouette instead of initials (for anon Fire Scouts). */
  anonymous?: boolean;
  /** Show a soft-pulsing primary dot in the top-right corner. */
  showIndicator?: boolean;
}

function AvatarImpl({
  size = 40,
  name = '',
  imageUrl,
  backgroundColor,
  anonymous = false,
  showIndicator = false,
}: AvatarProps) {
  const { colors } = useTheme();

  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // `action`, not `primaryStrong`: Design B spends colour on category
  // (progress/risk), and a green disc behind every author competed with the
  // category dot on the same feed row. colors.ts calls primaryStrong a
  // "legacy CTA fill — migrate call sites to `action`"; this was a call site
  // that migration never reached, because the visual audit masks avatars and
  // no baseline could show it.
  //
  // The initials MUST take `actionOn`, not white: `action` inverts by theme
  // (#161615 light, #ececea dark), so fixed white disappears in dark.
  const bgColor = backgroundColor || (anonymous ? colors.surfaceElevated : colors.action);
  const fontSize = size * 0.4;
  const iconSize = size * 0.7;
  const dotSize = Math.max(8, Math.round(size * 0.25));

  const pulse = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!showIndicator) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 1200,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 1200,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [showIndicator, pulse]);

  const inner = (() => {
    if (imageUrl && !anonymous) {
      return (
        <Image
          source={imageUrl}
          style={[
            styles.image,
            {
              width: size,
              height: size,
              borderRadius: size / 2,
              backgroundColor: colors.avatarPlaceholder,
            },
          ]}
          contentFit="cover"
          cachePolicy="memory-disk"
          transition={150}
          // Only the PHOTO is masked for the visual audit — an uploaded image
          // is live data. The initials and anonymous discs below deliberately
          // are NOT: they derive from the signed-in user, so they're
          // deterministic, and masking them is what let a green fill survive
          // the whole re-skin invisible to every baseline.
          // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
          dataSet={{ visualmask: 'true' }}
        />
      );
    }

    if (anonymous) {
      return (
        <View
          style={[
            styles.placeholder,
            {
              width: size,
              height: size,
              borderRadius: size / 2,
              backgroundColor: bgColor,
            },
          ]}>
          <UserCircle2 size={iconSize} color={colors.textSecondary} strokeWidth={1.5} />
        </View>
      );
    }

    return (
      <View
        style={[
          styles.placeholder,
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: bgColor,
          },
        ]}>
        <Text style={[styles.initials, { fontSize, color: colors.actionOn }]}>
          {initials || '?'}
        </Text>
      </View>
    );
  })();

  if (!showIndicator) return inner;

  const scale = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.15] });
  const opacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 0.6] });

  return (
    <View style={{ width: size, height: size }}>
      {inner}
      <Animated.View
        pointerEvents="none"
        // The pulse runs through RN Animated, not CSS, so the visual audit's
        // `animation: none` mask cannot freeze it — the shot catches the dot
        // at whatever opacity the loop happens to be at. That drift is the
        // only thing standing between the static screens and an exact-match
        // baseline, so hide the dot outright for the audit.
        // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
        dataSet={{ visualhide: 'true' }}
        style={[
          styles.indicator,
          {
            width: dotSize,
            height: dotSize,
            borderRadius: dotSize / 2,
            backgroundColor: colors.primary,
            borderColor: colors.background,
            transform: [{ scale }],
            opacity,
          },
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  image: {},
  placeholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  initials: {
    fontFamily: TYPOGRAPHY.button.fontFamily,
  },
  indicator: {
    position: 'absolute',
    top: 0,
    right: 0,
    borderWidth: 2,
  },
});

export const Avatar = React.memo(AvatarImpl);
